import { useEffect, useState } from "react";
import { toast } from "sonner";
import { PhoneCall } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Label } from "./ui/label";

/**
 * Opens from a missed or running-late appointment's row's "Mark Contacted" action — logs
 * what happened when the desk followed up. Free text, unlike other
 * dispositions, since "what the patient said on the phone" doesn't fit a
 * fixed list. Check-in for a missed row lives in the "..." menu instead,
 * not here — completion is automatic now (see checkIn on the backend),
 * there's no manual "Mark Complete" anywhere.
 */
export default function MarkContactedModal({ open, onOpenChange, appointment, onMarkContacted }) {
  const [disposition, setDisposition] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setDisposition("");
  }, [open]);

  const handleMarkContacted = async () => {
    if (!disposition.trim()) {
      toast.error("Add a note about what happened when you contacted them.");
      return;
    }
    setSaving(true);
    try {
      await onMarkContacted(appointment, disposition.trim());
      onOpenChange(false);
    } finally {
      setSaving(false);
    }
  };

  if (!appointment) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PhoneCall className="h-5 w-5 text-primary" />
            {appointment.missed ? "Missed Appointment" : "Running Late"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="rounded-md border border-border bg-muted/30 px-3 py-2">
            <p className="text-sm font-medium">{appointment.patient_id?.name}</p>
            <p className="text-xs text-muted-foreground">
              {appointment.doctor_id?.name} · {appointment.slot_time} · {appointment.iso_date}
            </p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="contact-disposition">What happened when you contacted them?</Label>
            <textarea
              id="contact-disposition"
              value={disposition}
              onChange={(e) => setDisposition(e.target.value)}
              placeholder="e.g. Called at 2:30 PM, no answer. Will try again tomorrow."
              rows={3}
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="button" disabled={saving} onClick={handleMarkContacted}>
            {saving ? "Saving…" : "Mark Contacted"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
